import { useEffect, useState } from "react";
import { CloseGreyIcon, UsersBlackIcon } from "../../../assets/icons";
import { TableInfoDTO } from "../../../models/table/table.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { useParams } from "react-router-dom";
import { tableService } from "../../../services/tableService";
import { isReadable } from "stream";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import { ReservationSettingsInfoDTO } from "../../../models/reservation/reservation.response";
import { TableShapeType } from "../../../models/base/TableShapeType";
import { reservationService } from "../../../services/reservationService";
import { ToLocalDateTimeString } from "../../../utils/dateFormater";

interface ReservationEditViewProps {
    onClose: () => void;
    id: string;
}

const ReservationEditView: React.FC<ReservationEditViewProps> = ({ onClose, id }) => {

    const [tables, setTables] = useState<TableInfoDTO[]>([]);
    const [quickSelectTables, setQuickSelectTables] = useState<TableInfoDTO[]>([]);
    const [selectedTableId, setSelectedTableId] = useState<string>("");
    const [reservationInfo, setReservationInfo] = useState<ReservationSettingsInfoDTO>();

    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    const { localId } = useParams<{ localId: string }>();


    useEffect(() => {
        fetchReservationInfo();
        fetchTables();
    }, []);

    useEffect(() => {
        if (reservationInfo?.numberOfPeople) {
            fetchQuickSelectTables();
        }
    }, [reservationInfo]);



    const handleConfirmReservation = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        if (!selectedTableId) {
            notify("error", "Molimo izaberite sto za potvrdu rezervacije.");
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.confirmReservation(localId, id, { tableId: selectedTableId });

            if (response.success) {
                notify("success", "Rezervacija je uspešno potvrđena.");
                onClose();
            } else {
                notify("error", "Greška pri potvrđivanju rezervacije.");
            }
        } catch (error: any) {
            notify("error", "Greška pri potvrđivanju rezervacije.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const fetchReservationInfo = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        if (!id) {
            notify("error", "ID rezervacije nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.getReservationInfoById(localId, id);

            if (response.success && response.data) {
                setReservationInfo(response.data);
            } else {
                notify("error", "Greška pri učitavanju informacija o rezervaciji.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju informacija o rezervaciji.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuickSelectTables = async () => {
        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await tableService.getTablesInfoByLocalId(localId, true, true, reservationInfo?.numberOfPeople, undefined, 3);

            if (response.success && response.data) {
                setQuickSelectTables(response.data);
            } else {
                notify("error", "Greška pri učitavanju stolova.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju stolova.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const fetchTables = async () => {

        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await tableService.getTablesInfoByLocalId(localId, true, true);

            if (response.success && response.data) {
                setTables(response.data);
            } else {
                notify("error", "Greška pri učitavanju stolova.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju stolova.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const tableShapeClass = (shape: TableShapeType) => {
        switch (shape) {
            case TableShapeType.Square:
                return "square";
            case TableShapeType.Round:
                return "circle";
            case TableShapeType.Rectangle:
                return "rectangle";
            default:
                return "";
        }
    };


    if (loading) {
        return <Loader />;
    }


    return (
        <section className="modal-section reservation-edit">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Potvrda Rezervacije - Dodela Stola</div>
                                <div className="description-text">
                                    Izaberite sto za potvrđivanje rezervacije
                                </div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>

                        <div className="modal-div-body grey-bg reservation">
                            <div className="modal-div-body-header">
                                <div className="modal-div-body-header-text">
                                    Podaci o gostu {reservationInfo?.isForAnotherPerson ? "za koga je rezervacija napravljena" : "koji je napravio rezervaciju"}<br />
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Ime i prezime</div>
                                    <div className="_0-95rem">{reservationInfo?.fullName}</div>
                                </div>
                                <div>
                                    <div className="description-text">Telefon</div>
                                    <div className="_0-95rem">{reservationInfo?.phoneNumber}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Email</div>
                                    <div className="_0-95rem">{reservationInfo?.email}</div>
                                </div>
                            </div>
                        </div>

                        {reservationInfo?.isForAnotherPerson && (
                            <div className="modal-div-body grey-bg reservation">
                                <div className="modal-div-body-header">
                                    <div className="modal-div-body-header-text">
                                        Podaci o korisniku koji je kreirao rezervaciju<br />
                                    </div>
                                </div>
                                <div className="div-block-39">
                                    <div>
                                        <div className="description-text">Ime i prezime</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.fullName}</div>
                                    </div>
                                    <div>
                                        <div className="description-text">Telefon</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.phoneNumber}</div>
                                    </div>
                                </div>
                                <div className="div-block-39">
                                    <div>
                                        <div className="description-text">Email</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.email}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="modal-div-body grey-bg reservation">
                            <div className="modal-div-body-header">
                                <div className="modal-div-body-header-text">Detalji rezervacije</div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Datum i vreme</div>
                                    <div className="_0-95rem">{reservationInfo?.reservationDate && ToLocalDateTimeString(reservationInfo.reservationDate)}</div>
                                </div>
                                <div>
                                    <div className="description-text">Do</div>
                                    <div className="_0-95rem">{reservationInfo?.departureDate ? ToLocalDateTimeString(reservationInfo.departureDate) : "/"}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Broj osoba</div>
                                    <div className="_0-95rem">{reservationInfo?.numberOfPeople}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div id="w-node-_4b97bfc6-9bc2-b241-5914-371c2f932b3d-1cee91cf">
                                    <div className="description-text">Napomena</div>
                                    <div className="input-div white-bg">
                                        <div className="_0-95rem">{reservationInfo?.description || "/"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-div-body reservation">
                            <div className="modal-div-body-header">
                                <div className="modal-div-body-header-text">Izaberite sto</div>
                                <div className="description-text">{tables.length} slobodna stola</div>
                            </div>
                            <div className="div-block-39">
                                <div id="w-node-_03e4bae5-71bf-8666-f6d3-a1c46af5a68d-1cee91cf">
                                    <div className="description-text">Brzo biranje</div>
                                    <div className="modal-reservation-quick-select-list-div">

                                        {quickSelectTables.length === 0 && (
                                            <div className="description-text">Nema dostupnih stolova za brzo biranje</div>
                                        )}

                                        {quickSelectTables.map((table) => (
                                            <div className={`modal-reservation-quick-select-div ${selectedTableId === table.id ? 'select' : ''}`} key={table.id} onClick={() => setSelectedTableId(table.id)}>
                                                <div className={`modal-reservation-quick-select-teble-shape ${tableShapeClass(table.shape)}`}>
                                                    <div>{table.number}</div>
                                                </div>
                                                <div>Sto {table.number}</div>
                                                <div className="div-block-40">
                                                    <img src={UsersBlackIcon} loading="lazy" alt="" className="icon-size-20" />
                                                    <div>{table.capacity}</div>
                                                </div>
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            </div>
                            <div className="div-block-55">
                                <div className="description-text">Ili izaberite sto ručno</div>
                                <select className="form-select auto-width w-select"
                                    value={selectedTableId}
                                    onChange={(e) => setSelectedTableId(e.target.value)}
                                >
                                    <option value="">Izaberi sto</option>

                                    {tables.map((table) => (
                                        <option key={table.id} value={table.id}>
                                            Sto {table.number} - Kapacitet: {table.capacity}
                                        </option>
                                    ))}

                                </select>
                            </div>
                        </div>
                        <div>
                            <div className="description-text">Kreirana: {reservationInfo?.createdAt && ToLocalDateTimeString(reservationInfo.createdAt)} </div>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={() => handleConfirmReservation()}>
                                <div>Potvrdi rezervaciju</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section >
    );
};

export default ReservationEditView;